import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AddressManager from '../components/AddressManager';
import MySubscriptions from '../components/MySubscriptions';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { KeyRound } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ProfilePage = () => {
  const { user } = useAuth();
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [saving, setSaving] = useState(false);

  const isGoogleAccount = !!user?.picture && !user?.has_password_hash; // heuristic; backend rejects if no hash

  const submitChangePassword = async (e) => {
    e?.preventDefault?.();
    if (newPwd.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (newPwd !== confirmPwd) { toast.error('New passwords do not match'); return; }
    if (oldPwd === newPwd) { toast.error('New password must be different from the current one'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/api/auth/change-password`,
        { old_password: oldPwd, new_password: newPwd },
        { withCredentials: true }
      );
      toast.success(data.message || 'Password updated');
      setOldPwd(''); setNewPwd(''); setConfirmPwd('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update password');
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen">
      <Header />

      <div className="container mx-auto px-4 py-12 pt-20">
        <h1 className="text-4xl font-black font-heading mb-8" data-testid="profile-heading">My Profile</h1>

        <div className="max-w-2xl space-y-6">
          <div className="p-8 rounded-2xl bg-white border border-[#E0E0E0]">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-bold text-muted-foreground block mb-1">Name</label>
                <p className="text-lg" data-testid="profile-name">{user?.name}</p>
              </div>
              <div>
                <label className="text-sm font-bold text-muted-foreground block mb-1">Email</label>
                <p className="text-lg" data-testid="profile-email">{user?.email}</p>
              </div>
              <div>
                <label className="text-sm font-bold text-muted-foreground block mb-1">Role</label>
                <p className="text-lg capitalize" data-testid="profile-role">{user?.role}</p>
              </div>
            </div>
          </div>

          {/* Change Password */}
          <div className="p-8 rounded-2xl bg-white border border-[#E0E0E0]" data-testid="change-password-section">
            <div className="flex items-center gap-2 mb-4">
              <KeyRound className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-black font-heading">Change Password</h2>
            </div>
            {isGoogleAccount ? (
              <p className="text-sm text-muted-foreground" data-testid="google-no-password">
                You signed in with Google. Manage your password through your Google account.
              </p>
            ) : (
              <form onSubmit={submitChangePassword} className="space-y-3">
                <div>
                  <Label htmlFor="old-pwd">Current password</Label>
                  <Input id="old-pwd" type="password" value={oldPwd} onChange={(e) => setOldPwd(e.target.value)} required autoComplete="current-password" data-testid="old-password-input" />
                </div>
                <div>
                  <Label htmlFor="new-pwd">New password (min 6 characters)</Label>
                  <Input id="new-pwd" type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} required minLength={6} autoComplete="new-password" data-testid="new-password-input" />
                </div>
                <div>
                  <Label htmlFor="confirm-pwd">Confirm new password</Label>
                  <Input id="confirm-pwd" type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} required minLength={6} autoComplete="new-password" data-testid="confirm-password-input" />
                </div>
                <Button type="submit" disabled={saving || !oldPwd || !newPwd || !confirmPwd} data-testid="change-password-submit">
                  {saving ? 'Updating…' : 'Update Password'}
                </Button>
              </form>
            )}
          </div>

          <div className="p-8 rounded-2xl bg-white border border-[#E0E0E0]">
            <AddressManager onSelect={() => {}} />
          </div>

          <MySubscriptions />
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProfilePage;
